import { useState, useRef } from 'react';
import AccountTypeSelector from './AccountTypeSelector';
import { supabase } from './supabase';
import { checkInviteCode, markCodeUsed } from './db';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',
  text2:'#9890b0',text3:'#55506a',error:'#ff6b6b',teal:'#1a8a76',
};

const inp = {
  background:T.s3,border:`1px solid ${T.border2}`,borderRadius:8,
  padding:'0.7rem 1rem',color:T.cream,fontSize:14,outline:'none',
  fontFamily:'inherit',width:'100%',
};

const CRAFTS = [
  'Actor','Model','Singer','Dancer','Photographer','Videographer',
  'Director','Producer','Stage Manager','Costume Designer','Stylist',
  'Lighting Designer','Sound Designer','Writer','Composer','Musician','DP',
];

const TOS_TEXT = `ARTIZAN STUDIO LLC — TERMS OF SERVICE & PRIVACY POLICY
Last updated: April 2026 | Draft — Attorney review pending

TERMS OF SERVICE

1. ACCEPTANCE
By creating an account, you agree to be bound by these Terms. If you do not agree, do not use the Platform.

2. ELIGIBILITY
You must be at least 18 years of age to create an account. By creating an account, you represent and warrant that you are 18 or older. Accounts may not be created by or for minors under any circumstances.

3. ACCOUNT TYPES
Artizan offers Artisan, Venue, and Industry Pro accounts. Each account type has specific verification requirements and feature access tied to your verification score (0–100).

Verification score tiers:
- Emerging (0–59): Browse castings, limited features
- Recognized (60–74): Apply to paid castings
- Verified Artisan (75–79): Full Artist Pro features activate
- Gold Badge (80–94): Priority placement in Talent Search
- Master Artisan (95–100): Top-tier platform access

4. PRICING
- Free: Basic profile, limited connections, read-only feed
- Artist Pro: $12/month introductory, rising to $15/month after 6 months with 30 days notice
- Artist Studio: $29/month — includes all Pro features plus Vizion collaborative pre-production

5. YOUR CONTENT
You retain ownership of all content you upload. By uploading, you grant Artizan a non-exclusive, worldwide, royalty-free license to display and distribute your content within the Platform and in promotional materials. You represent that you own or have rights to all content you post.

6. PROHIBITED CONDUCT
You agree not to:
- Upload content you do not own or have rights to use
- Impersonate any person or entity
- Use the Platform to harass, threaten, or intimidate any user
- Circumvent the verification system or submit false verification information
- Upload any content that depicts minors in a sexual context — this results in immediate termination, reporting to NCMEC, and referral to law enforcement
- Solicit payments outside the Platform's payment system

7. VIZION
Vizion is Artizan's collaborative pre-production storyboarding tool, available to Artist Studio subscribers. Board owners retain ownership of all creative content. Collaborators receive a limited, revocable license to view and contribute to invited boards only.

8. PAYMENTS & FEES
- Bookings and casting: 12% platform fee
- Marketplace transactions: 5–10%
- Live stream ticketing: 12% of gross ticket revenue
- All payments processed through Stripe

9. TERMINATION
Artizan may suspend or terminate any account for violation of these Terms. Certain violations (CSAM, targeted harassment, fraud) result in immediate permanent termination with no appeal.

10. LIMITATION OF LIABILITY
THE PLATFORM IS PROVIDED "AS IS." TO THE FULLEST EXTENT PERMITTED BY LAW, ARTIZAN'S TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID IN THE 12 MONTHS PRECEDING THE CLAIM OR $100.

11. GOVERNING LAW
These Terms are governed by the laws of the Commonwealth of Pennsylvania. Disputes shall be resolved by binding arbitration in Bethlehem, Pennsylvania.

12. CHANGES
Artizan may modify these Terms at any time. Material changes will be communicated via email at least 30 days before taking effect.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

PRIVACY POLICY

1. INFORMATION WE COLLECT
- Account info: name, email, date of birth, password
- Profile info: biography, location, craft disciplines, portfolio media, rate cards
- Verification info: documents, social proof links, credits, co-signatures
- Financial info: banking details stored encrypted, never shared without consent
- Usage data: pages viewed, features used, search queries
- Location data: GPS only when using Aisle discovery features, with explicit permission
- Vizion board content: storyboard frames, shot notes, collaborator contributions

2. HOW WE USE YOUR INFORMATION
- To provide, operate, and improve the Platform
- To verify your identity and calculate your verification score
- To facilitate connections, bookings, castings, and transactions
- To process payments and send transaction confirmations
- To detect fraud, abuse, and violations of our Terms
- To comply with legal obligations including mandatory reporting

3. WE DO NOT SELL YOUR DATA
Artizan does not sell your personal information to third parties. The Platform operates primarily on subscription revenue and transaction fees. Artizan may work with curated sponsors whose placements appear within the Platform; however, sponsors do not receive access to your personal information, and you will never be targeted based on your data.

4. DATA SHARING
We share your information only with:
- Other users, as necessary to facilitate connections and transactions
- Stripe for payment processing
- Supabase for database hosting
- Law enforcement when required by law or to prevent harm

5. VERIFICATION SCORE PRIVACY
Your verification score is private. It is visible to you and to Industry Pro subscribers who access your profile through Talent Search. It is never displayed publicly.

6. DATA SECURITY
- AES-256-GCM encryption for stored files
- bcrypt password hashing
- TLS encryption for all data in transit
- Row-level security on all database tables
- PhotoDNA scanning on all uploaded media before public accessibility

7. YOUR RIGHTS
- Access: request a copy of your data via Settings > Privacy
- Correction: update your profile at any time
- Deletion: request account deletion via Settings > Privacy (30-day grace period)
- California residents have additional rights under CCPA
- EU/EEA residents have rights under GDPR — contact privacy@artizancraft.app

8. DATA RETENTION
- Account data: retained for account life plus 30 days after deletion
- Transaction records: 7 years (IRS requirement)
- Audit logs: 3 years

9. CHILDREN'S PRIVACY
The Platform is not directed to children under 18. We do not knowingly collect data from minors. Accounts found to belong to minors will be terminated immediately.

10. CONTACT
Privacy: privacy@artizancraft.app
Legal: legal@artizancraft.app
DMCA: dmca@artizancraft.app
Address: 715 Aaron Street, Bethlehem, Pennsylvania 18015`;

function passwordStrength(pw) {
  if (!pw) return { label: '', color: '', width: '0%' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: 'Weak', color: T.error, width: '25%' };
  if (score <= 3) return { label: 'Medium', color: T.gold, width: '60%' };
  return { label: 'Strong', color: T.teal, width: '100%' };
}

function TosModal({ onAccept, onDecline }) {
  const [scrolled, setScrolled] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 40) setScrolled(true);
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:3000,background:'rgba(6,6,13,0.97)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',fontFamily:"'DM Sans','Jost',sans-serif"}}>
      <div style={{width:'100%',maxWidth:560,background:T.s1,border:`1px solid ${T.border2}`,borderRadius:16,display:'flex',flexDirection:'column',maxHeight:'90vh'}}>
        {/* Header */}
        <div style={{padding:'1.25rem 1.5rem',borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
            <div style={{width:28,height:28,borderRadius:7,background:`linear-gradient(135deg,${T.gold},${T.gold2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800,color:T.bg}}>A</div>
            <span style={{color:T.gold,fontWeight:700,fontSize:14}}>Artizan Studio LLC</span>
          </div>
          <div style={{color:T.cream,fontWeight:700,fontSize:16}}>Terms of Service & Privacy Policy</div>
          <div style={{color:T.text3,fontSize:11,marginTop:4}}>Please read carefully and scroll to the bottom to accept.</div>
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{flex:1,overflowY:'auto',padding:'1.25rem 1.5rem'}}
        >
          <pre style={{color:T.text2,fontSize:11,lineHeight:1.8,whiteSpace:'pre-wrap',fontFamily:'inherit',margin:0}}>
            {TOS_TEXT}
          </pre>
          <div style={{height:40}}/>
        </div>

        {/* Footer */}
        <div style={{padding:'1rem 1.5rem',borderTop:`1px solid ${T.border}`,flexShrink:0}}>
          {!scrolled && (
            <div style={{fontSize:11,color:T.text3,textAlign:'center',marginBottom:10}}>
              ↓ Scroll to the bottom to accept
            </div>
          )}
          <div style={{display:'flex',gap:10}}>
            <button onClick={onDecline} style={{flex:1,background:'none',border:`1px solid ${T.border2}`,borderRadius:8,padding:'0.75rem',fontSize:13,color:T.text2,cursor:'pointer',fontFamily:'inherit'}}>
              Decline
            </button>
            <button
              onClick={onAccept}
              disabled={!scrolled}
              style={{flex:2,background:scrolled?`linear-gradient(135deg,${T.gold},${T.gold2})`:T.s3,border:'none',borderRadius:8,padding:'0.75rem',fontSize:14,fontWeight:700,color:scrolled?T.bg:T.text3,cursor:scrolled?'pointer':'default',fontFamily:'inherit',opacity:scrolled?1:0.5,transition:'all 0.3s'}}
            >
              I Agree — Continue →
            </button>
          </div>
          <div style={{fontSize:10,color:T.text3,textAlign:'center',marginTop:8}}>
            By accepting, you confirm you are 18+ and agree to be bound by these terms.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Auth({ onAuth }) {
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState(1);
  const [showAccountTypeSelector, setShowAccountTypeSelector] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [showTos, setShowTos] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [handle, setHandle] = useState('');
  const [name, setName] = useState('');
  const [selectedCrafts, setSelectedCrafts] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [codeValid, setCodeValid] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const strength = passwordStrength(password);

  const toggleCraft = (craft) => {
    setSelectedCrafts(prev =>
      prev.includes(craft) ? prev.filter(c => c !== craft) : [...prev, craft]
    );
  };

  const handleCheckCode = async () => {
    if (!inviteCode.trim()) return;
    setCheckingCode(true);
    const result = await checkInviteCode(inviteCode);
    setCodeValid(result ? true : false);
    setCheckingCode(false);
  };

  const validateStep1 = () => {
    if (!name.trim()) { setError('Please enter your display name.'); return false; }
    if (!handle.trim()) { setError('Please choose a handle.'); return false; }
    if (!email.trim() || !email.includes('@')) { setError('Please enter a valid email.'); return false; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return false; }
    if (codeValid !== true) { setError('Please enter a valid invite code to continue.'); return false; }
    return true;
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError('Please enter your email and password.'); return; }
    setLoading(true); setError(null);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    else onAuth(data.user);
    setLoading(false);
  };

  const handleSignup = async () => {
    setLoading(true); setError(null);
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    if (data.user) {
      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        handle: handle.startsWith('@') ? handle : '@' + handle,
        display_name: name,
        crafts: selectedCrafts,
        email_verified: true,
        tos_accepted_at: new Date().toISOString(),
      });
      if (profileError) {
        if (profileError.message.includes('duplicate') || profileError.code === '23505') {
          setError('That handle is already taken. Please choose another.');
        } else {
          setError('Failed to create profile. Please try again.');
        }
        await supabase.auth.admin.deleteUser(data.user.id).catch(() => {});
        setLoading(false);
        return;
      }
      await markCodeUsed(inviteCode, data.user.id);
      setPendingUserId(data.user.id);
      setShowAccountTypeSelector(true);
    }
    setLoading(false);
  };

  const handleAccountTypeComplete = async (accountData) => {
    if (!pendingUserId) return;
    await supabase.from('profiles').update({
      account_type: accountData.account_type,
      account_subtype: accountData.account_subtype,
      business_name: accountData.business_name,
      ein: accountData.ein,
      ein_verified: accountData.ein_verified,
      ein_verification_status: accountData.ein_verification_status,
      crafts_declared: accountData.crafts_declared,
      portfolio_uploaded: accountData.portfolio_uploaded,
      identity_gate_passed: accountData.identity_gate_passed,
    }).eq('id', pendingUserId);
    setShowAccountTypeSelector(false);
    setPendingUserId(null);
    setMessage('Account created! Check your email and click the confirmation link to activate your account.');
    setMode('login');
  };

  const switchMode = (m) => {
    setMode(m); setStep(1); setError(null); setMessage(null);
    setEmail(''); setPassword(''); setHandle(''); setName('');
    setSelectedCrafts([]); setInviteCode(''); setCodeValid(null);
    if (m === 'signup' && !tosAccepted) setShowTos(true);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true); setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://artizancraft.app',
    });
    if (error) setError(error.message);
    else setMessage('Password reset email sent. Check your inbox.');
    setLoading(false);
  };

  if (showAccountTypeSelector) {
    return <AccountTypeSelector onComplete={handleAccountTypeComplete} />;
  }

  return (
    <>
      {showTos && (
        <TosModal
          onAccept={() => { setTosAccepted(true); setShowTos(false); }}
          onDecline={() => { setShowTos(false); setMode('login'); }}
        />
      )}

      <div style={{minHeight:'100vh',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Sans','Jost',sans-serif",padding:'1rem'}}>
        <div style={{width:'100%',maxWidth:460,background:T.s1,border:`1px solid ${T.border2}`,borderRadius:16,padding:'2.5rem'}}>

          {/* Logo */}
          <div style={{textAlign:'center',marginBottom:28}}>
            <div style={{width:52,height:52,borderRadius:14,margin:'0 auto 12px',background:`linear-gradient(135deg,${T.gold},${T.gold2})`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'serif',fontSize:28,fontWeight:700,color:T.bg}}>A</div>
            <h1 style={{fontFamily:'Georgia,serif',fontSize:26,fontWeight:700,color:T.gold,marginBottom:4}}>Artizan</h1>
            <p style={{fontSize:13,color:T.text2}}>
              {mode==='login' ? 'Welcome back' : step===1 ? 'Create your account — Step 1 of 2' : 'Almost there — Step 2 of 2'}
            </p>
          </div>

          {/* Progress bar for signup */}
          {mode==='signup' && (
            <div style={{background:T.s3,borderRadius:4,height:4,marginBottom:24,overflow:'hidden'}}>
              <div style={{height:'100%',borderRadius:4,background:`linear-gradient(90deg,${T.gold},${T.gold2})`,width:step===1?'50%':'100%',transition:'width .3s ease'}}/>
            </div>
          )}

          {/* Error / message */}
          {error && <div style={{background:'rgba(255,107,107,0.12)',border:`1px solid ${T.error}`,borderRadius:8,padding:'0.75rem',fontSize:13,color:T.error,marginBottom:16}}>{error}</div>}
          {message && <div style={{background:'rgba(26,138,118,0.12)',border:`1px solid ${T.teal}`,borderRadius:8,padding:'0.75rem',fontSize:13,color:T.teal,marginBottom:16}}>{message}</div>}

          {/* LOGIN */}
          {mode==='login' && (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <input placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inp} />
              <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                <input
                  placeholder="Password"
                  type={showPassword?'text':'password'}
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&handleLogin()}
                  style={{...inp,paddingRight:'2.5rem'}}
                />
                <button type="button" onClick={()=>setShowPassword(!showPassword)} style={{position:'absolute',right:10,background:'none',border:'none',cursor:'pointer',color:T.text2,fontSize:18,display:'flex',alignItems:'center',padding:4}}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              <button onClick={handleLogin} disabled={loading} style={{background:`linear-gradient(135deg,${T.gold},${T.gold2})`,border:'none',borderRadius:8,padding:'0.8rem',fontSize:14,fontWeight:700,color:T.bg,cursor:'pointer',fontFamily:'inherit',opacity:loading?0.7:1,marginTop:4}}>
                {loading ? 'Logging in...' : 'Log In'}
              </button>
              <div style={{textAlign:'right',marginTop:4}}>
                <span onClick={()=>switchMode('forgot')} style={{fontSize:12,color:T.text2,cursor:'pointer'}}>Forgot password?</span>
              </div>
            </div>
          )}

          {/* FORGOT PASSWORD */}
          {mode==='forgot' && (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <p style={{fontSize:13,color:T.text2,marginBottom:4}}>Enter your email and we'll send you a reset link.</p>
              <input placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inp} />
              <button onClick={handleForgotPassword} disabled={loading} style={{background:`linear-gradient(135deg,${T.gold},${T.gold2})`,border:'none',borderRadius:8,padding:'0.8rem',fontSize:14,fontWeight:700,color:T.bg,cursor:'pointer',fontFamily:'inherit',opacity:loading?0.7:1}}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div style={{textAlign:'center',fontSize:13,color:T.text2}}>
                <span onClick={()=>switchMode('login')} style={{color:T.gold,cursor:'pointer',fontWeight:600}}>back to login</span>
              </div>
            </div>
          )}

          {/* SIGNUP STEP 1 */}
          {mode==='signup' && step===1 && (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{marginBottom:4}}>
                <div style={{display:'flex',gap:8}}>
                  <input
                    placeholder="Invite code"
                    value={inviteCode}
                    onChange={e=>{setInviteCode(e.target.value.toUpperCase());setCodeValid(null);}}
                    style={{...inp,flex:1,letterSpacing:'0.1em',fontWeight:600,
                      borderColor: codeValid === true ? T.teal : codeValid === false ? T.error : T.border2
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCheckCode}
                    disabled={!inviteCode.trim() || checkingCode}
                    style={{background:T.s3,border:`1px solid ${T.border2}`,borderRadius:8,padding:'0 14px',fontSize:13,color:T.text2,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap',opacity:!inviteCode.trim()?0.5:1}}
                  >
                    {checkingCode ? '...' : 'Check'}
                  </button>
                </div>
                {codeValid === true && <div style={{fontSize:11,color:T.teal,marginTop:4}}>✓ Valid invite code</div>}
                {codeValid === false && <div style={{fontSize:11,color:T.error,marginTop:4}}>✕ Invalid or already used code</div>}
              </div>
              <input placeholder="Display name (e.g. Jerone Arnold Darden)" value={name} onChange={e=>setName(e.target.value)} style={inp} />
              <input placeholder="@handle (your unique username)" value={handle} onChange={e=>setHandle(e.target.value)} style={inp} />
              <input placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inp} />
              <div>
                <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                  <input
                    placeholder="Password (min 8 characters)"
                    type={showPassword?'text':'password'}
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    style={{...inp,paddingRight:'2.5rem'}}
                  />
                  <button type="button" onClick={()=>setShowPassword(!showPassword)} style={{position:'absolute',right:10,background:'none',border:'none',cursor:'pointer',color:T.text2,fontSize:18,display:'flex',alignItems:'center',padding:4}}>
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
                {password && (
                  <div style={{marginTop:6}}>
                    <div style={{background:T.s3,borderRadius:4,height:3,overflow:'hidden'}}>
                      <div style={{height:'100%',borderRadius:4,background:strength.color,width:strength.width,transition:'all .3s'}}/>
                    </div>
                    <div style={{fontSize:11,color:strength.color,marginTop:3}}>{strength.label} password</div>
                  </div>
                )}
              </div>
              <div style={{fontSize:11,color:T.text3,padding:'0.5rem 0.75rem',background:T.s2,borderRadius:8,border:`1px solid ${T.border}`}}>
                ✓ You have agreed to our Terms of Service and Privacy Policy
              </div>
              <button
                onClick={() => { setError(null); if (validateStep1()) setStep(2); }}
                style={{background:`linear-gradient(135deg,${T.gold},${T.gold2})`,border:'none',borderRadius:8,padding:'0.8rem',fontSize:14,fontWeight:700,color:T.bg,cursor:'pointer',fontFamily:'inherit',marginTop:4}}
              >
                Continue →
              </button>
            </div>
          )}

          {/* SIGNUP STEP 2 */}
          {mode==='signup' && step===2 && (
            <div>
              <p style={{fontSize:13,color:T.text2,marginBottom:14,lineHeight:1.6}}>
                Select all the crafts that describe your practice. You can always update this later.
              </p>
              <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:20}}>
                {CRAFTS.map(craft => {
                  const selected = selectedCrafts.includes(craft);
                  return (
                    <button key={craft} onClick={()=>toggleCraft(craft)} style={{padding:'6px 14px',borderRadius:20,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',background:selected?`${T.gold}20`:T.s3,border:`1px solid ${selected?T.gold:T.border2}`,color:selected?T.gold:T.text2,transition:'all .15s'}}>
                      {craft}
                    </button>
                  );
                })}
              </div>
              {selectedCrafts.length === 0 && (
                <p style={{fontSize:12,color:T.text3,marginBottom:14}}>Select at least one craft to continue.</p>
              )}
              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>{setStep(1);setError(null);}} style={{flex:1,background:'none',border:`1px solid ${T.border2}`,borderRadius:8,padding:'0.75rem',fontSize:13,fontWeight:600,color:T.text2,cursor:'pointer',fontFamily:'inherit'}}>← Back</button>
                <button
                  onClick={handleSignup}
                  disabled={loading || selectedCrafts.length===0}
                  style={{flex:2,background:`linear-gradient(135deg,${T.gold},${T.gold2})`,border:'none',borderRadius:8,padding:'0.75rem',fontSize:14,fontWeight:700,color:T.bg,cursor:selectedCrafts.length>0?'pointer':'default',fontFamily:'inherit',opacity:loading||selectedCrafts.length===0?0.6:1}}
                >
                  {loading ? 'Creating account...' : 'Create My Account ✦'}
                </button>
              </div>
            </div>
          )}

          {/* Switch mode */}
          <div style={{textAlign:'center',marginTop:20,fontSize:13,color:T.text2}}>
            {mode==='login'
              ? <><span>Don't have an account? </span><span onClick={()=>switchMode('signup')} style={{color:T.gold,cursor:'pointer',fontWeight:600}}>Sign up</span></>
              : <><span>Already have an account? </span><span onClick={()=>switchMode('login')} style={{color:T.gold,cursor:'pointer',fontWeight:600}}>Log in</span></>
            }
          </div>

          <div style={{textAlign:'center',marginTop:14,fontSize:11,color:T.text3}}>
            Verification is always free. Your data is never sold.
          </div>
        </div>
      </div>
    </>
  );
}
